import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ErrorHandler,
  Inject,
  Injectable,
  Optional,
  PLATFORM_ID,
} from '@angular/core';
import { REQUEST, RESPONSE } from '@nguniversal/express-engine/tokens';
import { Request, Response } from 'express';
import { ErrorTracking } from './error-tracking.service';
import { TRACKING_CONFIG } from './tracking.config';
import { TrackingConfig } from './tracking.interface';

@Injectable()
export class CustomErrorHandler extends ErrorHandler {
  isBrowser: boolean;

  constructor(
    private errorTracking: ErrorTracking,
    @Inject(TRACKING_CONFIG) private config: TrackingConfig,
    @Inject(PLATFORM_ID) private platformId: Object,
    @Optional()
    @Inject(REQUEST)
    private request: Request & { _SSR_handleError(error: any): void },
    @Optional() @Inject(RESPONSE) private response: Response,
  ) {
    super();
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  handleError(value: any): void {
    // Backport of https://github.com/angular/angular/pull/31443 to unwrap
    // unhandled Promises
    // FIXME: remove when https://github.com/angular/angular/pull/31443 has
    // landed & we've upgraded.
    let error: any;
    if (
      value.rejection &&
      (value.rejection instanceof Error || value.rejection.message)
    ) {
      error = value.rejection;
    } else {
      error = value;
    }

    // When on the server, abort & return a 500
    if (isPlatformServer(this.platformId)) {
      // The Angular Router currently throws on URLs with scripts tags or
      // invalid characters
      // See https://github.com/angular/angular/issues/21032
      if (
        error.message.includes('Error: Cannot match any routes. URL Segment: ')
      ) {
        this.response.status(400);
      } else {
        this.response.status(500);
      }
      this.request._SSR_handleError(error);
      return;
    }
    // When on the browser, report to Sentry
    // We don't log backend errors in Sentry as there are already logged in
    // Kibana
    if (!(error instanceof HttpErrorResponse)) {
      if (error instanceof Error) {
        this.errorTracking.captureException(error);
      } else {
        this.errorTracking.captureMessage(error.message || error);
      }
    }
    if (
      error instanceof HttpErrorResponse &&
      error.error &&
      error.error.error &&
      error.error.error.code &&
      error.error.request_id &&
      this.config.kibanaBaseUrl
    ) {
      console.error(
        `${error.error.error.code}: ${this.config.kibanaBaseUrl}/app/kibana#/discover?_g=(time:(from:now-3d))&_a=(query:(language:lucene,query:'request_id:${error.error.request_id}'))`
          .replace(/\(/g, '%28')
          .replace(/\)/g, '%29'),
      );
    }
    super.handleError(error);
  }
}
